/**
 * useCreator — 创作者公开主页 hooks
 */
import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  creatorsApi,
  type CreatorWorksOrderBy,
  type SilkCreatorContestAward,
  type SilkCreatorProfile,
  type SilkCreatorPublicCopyright,
  type SilkMarketplaceListing,
  type SilkWork,
} from '@/api'

interface ProfileState {
  profile: SilkCreatorProfile | null
  loading: boolean
  error: string | null
  notFound: boolean
}

const PROFILE_INIT: ProfileState = {
  profile: null,
  loading: false,
  error: null,
  notFound: false,
}

export type CreatorProfileKey =
  | { id: string | number | null; username?: null }
  | { id?: null; username: string | null }

export function useCreatorProfile(key: CreatorProfileKey) {
  const id = 'id' in key && key.id != null ? key.id : null
  const username = 'username' in key && key.username ? key.username : null

  const [state, setState] = useState<ProfileState>(PROFILE_INIT)

  const refresh = useCallback(async () => {
    if ((id == null || id === '') && !username) {
      setState({ ...PROFILE_INIT })
      return
    }
    setState((s) => ({ ...s, loading: true, error: null, notFound: false }))
    try {
      const profile =
        username != null && username !== ''
          ? await creatorsApi.profileByUsername(username)
          : await creatorsApi.profile(id!)
      setState({ profile, loading: false, error: null, notFound: false })
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState({ ...PROFILE_INIT, notFound: true })
        return
      }
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setState((s) => ({ ...s, loading: false, error: msg }))
    }
  }, [id, username])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { ...state, refresh }
}

export function useCreatorFollow(profile: SilkCreatorProfile | null, onChanged: () => void) {
  const [pending, setPending] = useState(false)

  const toggle = useCallback(async () => {
    if (!profile || pending) return
    setPending(true)
    try {
      if (profile.is_following) {
        await creatorsApi.unfollow(profile.id)
      } else {
        await creatorsApi.follow(profile.id)
      }
      onChanged()
    } finally {
      setPending(false)
    }
  }, [profile, pending, onChanged])

  return { pending, toggle }
}

interface WorksState {
  items: SilkWork[]
  total: number
  loading: boolean
  error: string | null
}

const WORKS_INIT: WorksState = { items: [], total: 0, loading: false, error: null }

export function useCreatorWorks(
  id: number | string | null,
  limit = 24,
  offset = 0,
  orderBy: CreatorWorksOrderBy = 'published_at',
) {
  const [state, setState] = useState<WorksState>(WORKS_INIT)

  const refresh = useCallback(async () => {
    if (id == null || id === '') {
      setState({ ...WORKS_INIT })
      return
    }
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const list = await creatorsApi.works(id, { limit, offset, order_by: orderBy })
      setState({ items: list.items, total: list.total, loading: false, error: null })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setState((s) => ({ ...s, loading: false, error: msg }))
    }
  }, [id, limit, offset, orderBy])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { ...state, refresh }
}

function useCreatorList<T>(
  id: number | string | null,
  kind: 'contests' | 'copyrights' | 'listings',
  enabled: boolean,
) {
  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!enabled || id == null || id === '') return
    setLoading(true)
    setError(null)
    try {
      const list =
        kind === 'contests'
          ? await creatorsApi.contests(id, { limit: 20, offset: 0 })
          : kind === 'copyrights'
            ? await creatorsApi.copyrights(id, { limit: 20, offset: 0 })
            : await creatorsApi.listings(id, { limit: 20, offset: 0 })
      setItems(list.items as T[])
      setTotal(list.total)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [enabled, id, kind])

  useEffect(() => {
    if (!enabled || id == null || id === '') {
      setItems([])
      setTotal(0)
      setError(null)
      setLoading(false)
      return
    }
    void refresh()
  }, [enabled, id, kind, refresh])

  return { items, total, loading, error, refresh }
}

export function useCreatorContests(id: number | string | null, enabled: boolean) {
  return useCreatorList<SilkCreatorContestAward>(id, 'contests', enabled)
}

export function useCreatorCopyrights(id: number | string | null, enabled: boolean) {
  return useCreatorList<SilkCreatorPublicCopyright>(id, 'copyrights', enabled)
}

export function useCreatorListings(id: number | string | null, enabled: boolean) {
  return useCreatorList<SilkMarketplaceListing>(id, 'listings', enabled)
}
